/**
 * Customers.new Session Replay Recorder
 *
 * Captures full DOM snapshot, MutationObserver DOM changes, and user
 * interactions (mouse movements, clicks, scrolls, inputs) using the rrweb engine.
 */
import { record } from 'rrweb';

export class SessionRecorder {
    constructor(options = {}) {
        this.appId = options.appId || (typeof document !== 'undefined' ? (document.querySelector('script[data-app-id]')?.getAttribute('data-app-id') || '') : '');
        this.businessId = options.businessId || '';
        this.serverUrl = (options.serverUrl || options.server || options.configUrl || options.apiUrl || (typeof document !== 'undefined' ? (document.querySelector('script[data-server]')?.getAttribute('data-server') || '') : '') || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');
        this.sessionId = options.sessionId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('__gr_sid') : '') || `s_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        this.visitorId = options.visitorId || (typeof localStorage !== 'undefined' ? localStorage.getItem('__gr_vid') : '') || `vid_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        this.flushIntervalMs = options.flushIntervalMs || 2500;
        this.maxBufferEvents = options.maxBufferEvents || 30;
        this.maskAllInputs = options.maskAllInputs || false;

        let savedChunkIdx = 0;
        try {
            const storedSession = sessionStorage.getItem('__gr_chunk_sid');
            if (storedSession === this.sessionId) {
                const parsed = parseInt(sessionStorage.getItem('__gr_chunk_idx') || '0', 10);
                if (!isNaN(parsed) && parsed >= 0) savedChunkIdx = parsed;
            } else {
                sessionStorage.setItem('__gr_chunk_sid', this.sessionId);
                sessionStorage.setItem('__gr_chunk_idx', '0');
            }
        } catch {}
        this._chunkIndex = savedChunkIdx;
        this._buffer = [];
        this._stopFn = null;
        this._timer = null;
        this._isRecording = false;
        this._boundFlush = this.flush.bind(this);
        this._boundUnloadFlush = this._onPageUnload.bind(this);
    }

    /**
     * Start DOM and interaction recording
     */
    start() {
        if (this._isRecording || typeof window === 'undefined') return;

        if (window.__customers_recorder && window.__customers_recorder._isRecording && window.__customers_recorder !== this) {
            return;
        }
        window.__customers_recorder = this;

        try {
            this._stopFn = record({
                emit: (event) => {
                    this._buffer.push(event);
                    if (this._buffer.length >= this.maxBufferEvents) {
                        this.flush();
                    }
                },
                // Privacy and performance settings
                maskAllInputs: this.maskAllInputs,
                maskInputOptions: {
                    password: true,
                },
                sampling: {
                    mousemove: 50, // capture mouse move every 50ms (smooth & lightweight)
                    mouseInteraction: true,
                    scroll: 100, // throttle scroll to 100ms
                    input: 'last',
                },
                inlineStylesheet: true,
                collectFonts: true,
                recordCanvas: false,
            });

            this._isRecording = true;

            // Immediate initial flush after DOM snapshot is taken to guarantee page is captured even on quick navigation
            setTimeout(() => {
                if (this._buffer.length > 0) {
                    this.flush();
                }
            }, 300);

            // Runtime error capture for Session Replay timeline & hasErrors tagging
            this._onError = (evt) => {
                const errMsg = evt.message || evt.error?.message || 'Unhandled Runtime Error';
                this._buffer.push({
                    type: 6, // Custom Plugin Event
                    data: {
                        plugin: 'rrweb/console@1',
                        payload: {
                            level: 'error',
                            trace: [errMsg, evt.error?.stack || ''],
                            payload: [errMsg],
                        },
                    },
                    timestamp: Date.now(),
                });
                this.flush();
            };
            this._onRejection = (evt) => {
                const errMsg = evt.reason?.message || String(evt.reason) || 'Unhandled Promise Rejection';
                this._buffer.push({
                    type: 6,
                    data: {
                        plugin: 'rrweb/console@1',
                        payload: {
                            level: 'error',
                            trace: [errMsg],
                            payload: [errMsg],
                        },
                    },
                    timestamp: Date.now(),
                });
                this.flush();
            };
            window.addEventListener('error', this._onError);
            window.addEventListener('unhandledrejection', this._onRejection);

            // Periodic buffer flush
            this._timer = setInterval(this._boundFlush, this.flushIntervalMs);

            // Flush remaining on tab hide or page navigation
            window.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this._onPageUnload();
                }
            });
            window.addEventListener('pagehide', this._boundUnloadFlush);
            window.addEventListener('beforeunload', this._boundUnloadFlush);
        } catch (err) {
            console.warn('[Customers.new Replay] Failed to initialize recorder:', err);
        }
    }

    /**
     * Flush buffered events to backend ingestion API
     */
    async flush() {
        if (this._buffer.length === 0 || !this.serverUrl) return;

        const eventsToSend = [...this._buffer];
        this._buffer = [];
        const currentChunk = this._chunkIndex++;
        try {
            sessionStorage.setItem('__gr_chunk_idx', String(this._chunkIndex));
        } catch {}

        const payload = {
            appId: this.appId,
            businessId: this.businessId,
            sessionId: this.sessionId,
            visitorId: this.visitorId,
            chunkIndex: currentChunk,
            events: eventsToSend,
            metadata: {
                url: window.location.href,
                title: document.title,
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight,
                },
                device: this._detectDevice(),
                browser: this._detectBrowser(),
                os: this._detectOS(),
            },
        };

        try {
            const res = await fetch(`${this.serverUrl}/api/replay/events`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                console.warn('[Customers.new Replay] Ingest responded with status', res.status);
            }
        } catch (err) {
            // Re-buffer on network failure
            this._buffer.unshift(...eventsToSend);
        }
    }

    /**
     * Emergency flush on page unload using keepalive fetch
     */
    _onPageUnload() {
        if (this._buffer.length === 0 || !this.serverUrl) return;

        const eventsToSend = [...this._buffer];
        this._buffer = [];
        const currentChunk = this._chunkIndex++;
        try {
            sessionStorage.setItem('__gr_chunk_idx', String(this._chunkIndex));
        } catch {}

        const payload = JSON.stringify({
            appId: this.appId,
            businessId: this.businessId,
            sessionId: this.sessionId,
            visitorId: this.visitorId,
            chunkIndex: currentChunk,
            events: eventsToSend,
            metadata: {
                url: window.location.href,
                title: document.title,
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight,
                },
                device: this._detectDevice(),
                browser: this._detectBrowser(),
                os: this._detectOS(),
            },
        });

        const targetUrl = `${this.serverUrl}/api/replay/events`;

        try {
            fetch(targetUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true,
            }).catch(() => {});
        } catch (_) {}
    }

    /**
     * Stop and cleanup recorder
     */
    stop() {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }

        if (typeof this._stopFn === 'function') {
            this._stopFn();
            this._stopFn = null;
        }

        this.flush();
        this._isRecording = false;

        if (typeof window !== 'undefined') {
            if (window.__customers_recorder === this) {
                window.__customers_recorder = null;
            }
            if (this._onError) window.removeEventListener('error', this._onError);
            if (this._onRejection) window.removeEventListener('unhandledrejection', this._onRejection);
            window.removeEventListener('pagehide', this._boundUnloadFlush);
            window.removeEventListener('beforeunload', this._boundUnloadFlush);
        }
    }

    _detectDevice() {
        const ua = navigator.userAgent;
        if (/Mobile|Android|iPhone|iPod/i.test(ua)) return 'mobile';
        if (/iPad|Tablet/i.test(ua)) return 'tablet';
        return 'desktop';
    }

    _detectBrowser() {
        const ua = navigator.userAgent;
        if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) return 'Chrome';
        if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
        if (/Firefox/i.test(ua)) return 'Firefox';
        if (/Edg/i.test(ua)) return 'Edge';
        return 'Browser';
    }

    _detectOS() {
        const ua = navigator.userAgent;
        if (/Mac/i.test(ua)) return 'macOS';
        if (/Win/i.test(ua)) return 'Windows';
        if (/Linux/i.test(ua)) return 'Linux';
        if (/Android/i.test(ua)) return 'Android';
        if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
        return 'Unknown';
    }
}
